/**
 * Ticket 03: Equipment Automated Power Patching Logic
 * Evaluates whether equipment is eligible for automated 2N PDU power inlet patching,
 * and formats human-friendly toast messages.
 */

export const POWER_CONSUMER_TYPES = ['server', 'storage', 'switch', 'router'] as const;

export interface ShouldOfferAutoPatchParams {
  equipment_type?: string;
  is_zero_u?: boolean;
  hasPdu?: boolean;
  isEdit?: boolean;
}

export interface PowerPatchConnectionItem {
  cable_id?: string;
  psu_port_id?: string;
  psu_port_name: string;
  pdu_id?: string;
  pdu_name: string;
  pdu_outlet_id?: string;
  pdu_outlet_name: string;
  feed?: string;
}

export interface AutoPatchPowerResponse {
  equipment_id: string;
  equipment_name: string;
  connections_created: number;
  connections: PowerPatchConnectionItem[];
  warnings?: string[];
}

/**
 * Checks if auto-patching toggle should be offered in the equipment form drawer.
 * Only offered for new mounts (not edits), non-Zero-U, power consumers, when rack has at least one PDU.
 */
export function shouldOfferAutoPowerPatch(params: ShouldOfferAutoPatchParams): boolean {
  if (params.isEdit) return false;
  if (params.is_zero_u) return false;
  if (params.hasPdu === false) return false;

  const eqType = (params.equipment_type || '').toLowerCase().trim();
  return (POWER_CONSUMER_TYPES as readonly string[]).includes(eqType);
}

/**
 * Formats a user-facing toast notification summarising the auto-patch result.
 */
export function formatAutoPatchToast(patchResult: AutoPatchPowerResponse): { message: string; warnings: string[] } {
  const warnings = patchResult.warnings || [];

  if (!patchResult.connections || patchResult.connections.length === 0) {
    const isAlreadyConnected = warnings.some(
      w => w.toLowerCase().includes('already') || w.toLowerCase().includes('уже')
    );
    if (isAlreadyConnected) {
      return {
        message: 'Все вводы питания оборудования уже подключены к PDU',
        warnings,
      };
    }
    const reason = warnings.length > 0 ? warnings[0] : 'нет доступных розеток или PDU';
    return {
      message: `Питание не подключено (${reason})`,
      warnings,
    };
  }

  const parts = patchResult.connections.map(
    c => `${c.psu_port_name} → ${c.pdu_name} (${c.pdu_outlet_name})`
  );

  return {
    message: `Питание подключено: ${parts.join(', ')}`,
    warnings,
  };
}
